import React, { type ComponentProps, useCallback } from 'react';

import PropTypes from 'prop-types';

import { useControlled, useForwardedRef } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import { type RenderElementType } from '@adsk/alloy-react-text-input';
import { stylePropType } from '@adsk/alloy-react-theme';

import {
  Input,
  RightAdornmentContainer,
  RightAdornmentText,
  ClearButton,
  SearchFieldIconButton,
} from './components';
import useKeyDown from './hooks/useKeyDown';

type SearchFieldProps = Omit<ComponentProps<typeof Input>, 'onChange'> & {
  loading?: boolean;
  inlineText?: string;
  onClear?: () => void;
  onSearch?: () => void;
  onChange?: (value: string) => void;
};

/**
 * A SearchField component with a magnifying glass and a clear input text button
 */
const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      disabled = false,
      loading = false,
      type = 'search',
      defaultValue,
      inlineText = '',
      onClear,
      onSearch,
      value: valueProp,
      onChange: onChangeProp,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef<HTMLInputElement>(ref);
    const theme = useTheme();
    const searchFieldTokens = theme.tokens?.component?.searchfield;
    const [value, onChange] = useControlled(
      defaultValue?.toString() ?? '',
      valueProp,
      onChangeProp,
    );

    const clearInput = useCallback(() => {
      onChange?.('');
      onClear?.();
    }, [onChange, onClear]);

    useKeyDown(inputRef.current, 'Escape', clearInput);
    useKeyDown(inputRef.current, 'Enter', () => onSearch?.());
    useKeyDown(inputRef.current, 'NumpadEnter', () => onSearch?.());

    const renderLeftElement: RenderElementType = useCallback(
      (magnifyingGlassProps) => (
        <SearchFieldIconButton
          {...magnifyingGlassProps}
          $tokens={searchFieldTokens}
          disabled={disabled}
          onClick={() => {
            inputRef.current?.focus();
            onSearch?.();
          }}
        />
      ),
      [disabled, inputRef, onSearch, searchFieldTokens],
    );

    const renderRightElement: RenderElementType = useCallback(
      ({ size: iconSize, ...rightContainerProps }) => (
        <RightAdornmentContainer
          {...rightContainerProps}
          $tokens={searchFieldTokens}
          style={{ ...rightContainerProps.style, pointerEvents: 'auto' }}
        >
          {inlineText && (
            <RightAdornmentText dir="auto" $tokens={theme.tokens}>
              {inlineText}
            </RightAdornmentText>
          )}
          {loading && (
            <ProgressRing
              data-testid="progress-ring"
              size={ProgressRing.SIZES.XSMALL}
            />
          )}
          {value && !disabled && (
            <ClearButton
              data-testid="SearchField__ClearButton"
              tabIndex={-1}
              size={iconSize}
              $tokens={searchFieldTokens}
              onClick={(e) => {
                e.preventDefault();
                clearInput();
              }}
            />
          )}
        </RightAdornmentContainer>
      ),
      [
        clearInput,
        disabled,
        inlineText,
        loading,
        value,
        searchFieldTokens,
        theme?.tokens,
      ],
    );

    return (
      <Input
        value={value}
        type={type}
        {...props}
        ref={inputRef}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        renderLeftElement={renderLeftElement}
        renderRightElement={
          value || inlineText || loading ? renderRightElement : undefined
        }
      />
    );
  },
);

SearchField.displayName = 'SearchField';

SearchField.propTypes = {
  /* Styles applied to the input element */
  style: stylePropType,
  /* Class applied to the input element */
  className: PropTypes.string,
  /* Toggle disabled state */
  disabled: PropTypes.bool,
  /* Toggle loading spinner */
  loading: PropTypes.bool,
  /* initial default text value */
  defaultValue: PropTypes.string,
  /* controlled input value */
  value: PropTypes.string,
  /* Child input's onChange handler */
  onChange: PropTypes.func,
  /* Any text to display at the right of the input,
   * intended to show the number of results returned from the search
   **/
  inlineText: PropTypes.string,
};
export default SearchField;
